import React, { useState, useEffect } from 'react';
import GeneratePDF from '../GeneratePDF';
// ⛔ Removed Capacitor TTS import
// import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { warmupAudio, playClick } from '../../audio/clicks'; // ← keep

function AssetWorkflow({ formUrl }) {
  const [pdfData, setPdfData] = useState(null);
  const [steps, setSteps] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // --- Simple Web Speech helper (WebView-friendly) ---
  const speak = (text) => {
    try {
      if (!('speechSynthesis' in window)) return;
      const utter = new SpeechSynthesisUtterance(text);

      // Try to pick an en-GB voice if available; otherwise default
      const voices = window.speechSynthesis.getVoices?.() || [];
      const gb = voices.find(v => /en-GB/i.test(v.lang)) || voices.find(v => /UK|en_GB/i.test(`${v.name} ${v.lang}`));
      if (gb) utter.voice = gb;

      utter.rate = 1.0;
      utter.pitch = 1.0;
      window.speechSynthesis.speak(utter);
    } catch (e) {
      // Silent fallback: if Web Speech is unavailable, do nothing
      // (You can re-enable Capacitor here if you ever want a hybrid.)
    }
  };

  // 🔊 Unlock audio + prime Web Speech once after first user gesture (browser + Vuplex)
  useEffect(() => {
    const unlock = () => {
      // prime click sounds
      warmupAudio();
      // prime TTS (muted, to satisfy user-gesture requirement)
      try {
        const u = new SpeechSynthesisUtterance('ready');
        u.volume = 0;
        window.speechSynthesis?.speak(u);
      } catch {}
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
  }, []);

  // (Optional) Nudge voices to load on some Android webviews
  useEffect(() => {
    const noop = () => window.speechSynthesis?.getVoices?.();
    window.speechSynthesis?.addEventListener?.('voiceschanged', noop);
    return () => window.speechSynthesis?.removeEventListener?.('voiceschanged', noop);
  }, []);

  useEffect(() => {
    const loadQuestionsFromXML = async (url) => {
      try {
        const response = await fetch(url);
        const xmlText = await response.text();
        const xmlDoc = new DOMParser().parseFromString(xmlText, 'application/xml');
        const questions = xmlDoc.getElementsByTagName('question');
        const loadedQuestions = Array.from(questions).map((question) => ({
          description: question.getElementsByTagName('description')[0].textContent,
          detail: question.getElementsByTagName('detail')[0].textContent,
        }));
        setSteps(
          loadedQuestions.map((q) => ({
            question: q.description,
            detail: q.detail,
            response: '',
            remarks: '',
            photo: null,
            photoTimestamp: '',
            videoFile: null,
            videoPreviewUrl: null,
            videoTimestamp: '',
          }))
        );
        setCurrentQuestionIndex(0);
      } catch (error) {
        console.error('Error loading questions:', error);
      }
    };

    if (formUrl) loadQuestionsFromXML(formUrl);
  }, [formUrl]);

  // 🔈 Speak the current step using Web Speech API (replaces Capacitor TTS)
  useEffect(() => {
    if (steps.length > 0 && currentQuestionIndex < steps.length) {
      speak(`${steps[currentQuestionIndex].question}: ${steps[currentQuestionIndex].detail}`);
    }
  }, [currentQuestionIndex, steps]); // was TextToSpeech.speak(...) before

  const handleNextSection = (e) => {
    if (e?.currentTarget?.disabled || e?.currentTarget?.getAttribute('aria-disabled') === 'true') return;
    playClick();
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex < steps.length) setCurrentQuestionIndex(nextIndex);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Image = e.target.result;
      const newSteps = [...steps];
      newSteps[currentQuestionIndex] = {
        ...newSteps[currentQuestionIndex],
        photo: base64Image,
        photoTimestamp: new Date().toLocaleString(),
      };
      setSteps(newSteps);
    };
    reader.readAsDataURL(file);
  };

  const handleVideoChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const newSteps = [...steps];
    newSteps[currentQuestionIndex] = {
      ...newSteps[currentQuestionIndex],
      videoFile: file,
      videoPreviewUrl: URL.createObjectURL(file),
      videoTimestamp: new Date().toLocaleString(),
    };
    setSteps(newSteps);
  };

  const handleRadioChange = (value, e) => {
    if (e?.currentTarget?.disabled || e?.currentTarget?.getAttribute('aria-disabled') === 'true') return;
    playClick();
    const newSteps = [...steps];
    newSteps[currentQuestionIndex].response = value;
    setSteps(newSteps);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    playClick();
    setPdfData([...steps]);
  };

  const getButtonStyle = (value) => ({
    padding: '10px',
    margin: '4px',
    backgroundColor: steps[currentQuestionIndex].response === value ? 'green' : 'gray',
    color: 'white',
  });

  const getFileButtonStyle = (fileType) => ({
    padding: '10px',
    margin: '4px',
    backgroundColor:
      (fileType === 'photo' && steps[currentQuestionIndex].photo) ||
      (fileType === 'video' && steps[currentQuestionIndex].videoFile)
        ? 'green'
        : 'gray',
    color: 'white',
  });

  return (
    <>
      <form onSubmit={handleSubmit}>
        <h2>Inspection Form</h2>
        {steps.length > 0 ? (
          <div>
            <p>{steps[currentQuestionIndex].question}</p>
            <p>{steps[currentQuestionIndex].detail}</p>

            <div>
              {['yes', 'no', 'ok', 'na', 'pass', 'fail'].map((resp) => (
                <button
                  type="button"
                  key={resp}
                  style={getButtonStyle(resp)}
                  onClick={(e) => handleRadioChange(resp, e)}
                >
                  {resp.toUpperCase()}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={(e) => {
                if (e.currentTarget.disabled || e.currentTarget.getAttribute('aria-disabled') === 'true') return;
                playClick();
                document.getElementById('photo').click();
              }}
              style={getFileButtonStyle('photo')}
            >
              +PHOTO
            </button>
            <input
              type="file"
              id="photo"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            <button
              type="button"
              onClick={(e) => {
                if (e.currentTarget.disabled || e.currentTarget.getAttribute('aria-disabled') === 'true') return;
                playClick();
                document.getElementById('video').click();
              }}
              style={getFileButtonStyle('video')}
            >
              +VIDEO
            </button>
            <input
              type="file"
              id="video"
              accept="video/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleVideoChange}
            />

            {steps[currentQuestionIndex].photo && (
              <div>
                <p>📷 Taken: {steps[currentQuestionIndex].photoTimestamp}</p>
                <img src={steps[currentQuestionIndex].photo} alt="preview" width="150" />
              </div>
            )}

            {steps[currentQuestionIndex].videoPreviewUrl && (
              <div>
                <p>🎥 Recorded: {steps[currentQuestionIndex].videoTimestamp}</p>
                <video width="250" controls src={steps[currentQuestionIndex].videoPreviewUrl} />
              </div>
            )}

            <button type="button" onClick={handleNextSection}>NEXT</button>
            <button type="submit">SUBMIT</button>
            <br />
          </div>
        ) : (
          <p>Loading form...</p>
        )}
      </form>

      {pdfData && <GeneratePDF formData={pdfData} />}
    </>
  );
}

export default AssetWorkflow;
